/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography, Typography2, breakpoints } from '@coursera/cds-core';

import type {
  EntWebsiteBlockValuePropQuery,
  EntWebsiteValuePropFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout from 'bundles/ent-website/contentful/core/BlockLayout';
import Image from 'bundles/ent-website/contentful/core/Image';
import { entWebsiteBlockValuePropQuery } from 'bundles/ent-website/queries/EntWebsiteValueProp';
import type { GraphqlCollectionItem } from 'bundles/ent-website/types';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';

type Props = BlockModuleInnerProps<EntWebsiteValuePropFragment>;

type CardProps = {
  card: GraphqlCollectionItem<EntWebsiteValuePropFragment['valuePropCardsCollection']>;
};

export function ValuePropCard({ card }: CardProps) {
  const styles = {
    card: css`
      flex-basis: calc(50% - var(--cds-spacing-400));
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      text-align: left;

      &:nth-child(2n + 1) {
        margin-right: var(--cds-spacing-800);
      }

      &:nth-child(odd):not(:nth-last-child(2)) {
        margin-bottom: var(--cds-spacing-800);
      }

      ${breakpoints.down('xs')} {
        flex-basis: 100%;

        &:nth-child(2n + 1) {
          margin-right: 0;
        }

        &:not(:last-child) {
          margin-bottom: var(--cds-spacing-800);
        }
      }
    `,

    icon: css`
      margin-bottom: var(--cds-spacing-400);
    `,

    cardHeadline: css`
      font-size: 1.25rem !important;
      line-height: 100%;
      letter-spacing: -3%;
      font-weight: 600;
      margin-bottom: var(--cds-spacing-100);
    `,
    cardDescription: css`
      font-size: 1rem !important;
      line-height: 150%;
      letter-spacing: -3%;
    `,
  };

  return (
    <div css={styles.card}>
      {card?.icon && <Image content={card.icon} css={styles.icon} />}
      <Typography2 component="p" color="inherit" css={styles.cardHeadline}>
        {card?.headline}
      </Typography2>
      <Typography2 component="p" color="inherit" css={styles.cardDescription}>
        {card?.description}
      </Typography2>
    </div>
  );
}

export function ValuePropModule({ content, assembly }: Props) {
  const { visibility } = assembly;
  const styles = {
    cards: css`
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    `,
    content: css`
      /* stylelint-disable-line no-empty-source */
    `,

    headline: css`
      font-size: 3rem !important;
      line-height: 100% !important;
      letter-spacing: -3% !important;
      margin-top: var(--cds-spacing-200);
      margin-bottom: var(--cds-spacing-400);

      ${breakpoints.down('xs')} {
        font-size: 2.25rem !important;
      }
    `,
    description: css`
      font-size: 1.25rem !important;
      line-height: 150% !important;
      letter-spacing: -2% !important;
      margin-bottom: var(--cds-spacing-800);

      ${breakpoints.down('xs')} {
        font-size: 1rem !important;
      }
    `,
  };

  const cardPanel = () =>
    visibility.valuePropCards ? (
      <div css={styles.cards}>
        {content.valuePropCardsCollection?.items.map((card) => card && <ValuePropCard key={card.slug} card={card} />)}
      </div>
    ) : null;

  const contentPanel = () => (
    <div css={styles.content}>
      {visibility.headline && content.headline && (
        <Typography variant="h2semibold" css={styles.headline} color="inherit">
          {content.headline}
        </Typography>
      )}

      {visibility.description && content.description && (
        <Typography2 component="p" variant="bodyPrimary" css={styles.description} color="inherit">
          {content.description}
        </Typography2>
      )}
    </div>
  );

  return <BlockLayout assembly={assembly} renderContent={contentPanel} renderImage={cardPanel} />;
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockValuePropModule = withBlockQuery<EntWebsiteValuePropFragment, EntWebsiteBlockValuePropQuery>(
  entWebsiteBlockValuePropQuery
)(ValuePropModule);
